import React from "react";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const [year, month] = dateString.split("-");
  const date = new Date(year, month - 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const BaseTemplate = ({ data }) => {
  const {
    personalInfo,
    summary,
    skills,
    experience,
    education,
    projects,
    certifications,
  } = data;

  return (
    <div
      className="w-full flex flex-col space-y-1.5 print:text-black print:space-y-1 relative z-10 px-0"
      style={{ pageBreakAfter: "avoid", pageBreakInside: "avoid" }}
    >
      {/* Header Section */}
      <header
        className="flex items-center justify-start mb-1 w-full px-2"
        style={{ pageBreakInside: "avoid" }}
      >
        {personalInfo?.profileImage &&
          (typeof personalInfo.profileImage === "string" ||
            personalInfo.profileImage instanceof Blob) && (
            <div className="flex-shrink-0 ml-0 mt-0">
              <img
                src={
                  typeof personalInfo.profileImage === "string"
                    ? personalInfo.profileImage
                    : URL.createObjectURL(personalInfo.profileImage)
                }
                className="w-16 h-16 rounded-full object-cover"
                alt="Profile Preview"
              />
            </div>
          )}

        <div className="text-center flex flex-col items-center ml-4 w-full">
          <h1 className="text-xl font-bold text-gray-900 leading-tight relative z-10 mb-0">
            {personalInfo.fullName}
          </h1>
          <h2 className="text-base font-semibold text-blue-600 leading-tight relative z-10 mt-0">
            {personalInfo.title}
          </h2>

          <div className="text-xs text-gray-600 space-x-1 leading-tight relative z-10 flex flex-wrap justify-center">
            {personalInfo?.email && (
              <a
                href={`mailto:${personalInfo.email}`}
                className="text-blue-600 hover:underline"
              >
                {personalInfo.email}
              </a>
            )}
            {personalInfo.phone && <span>•</span>}
            <span>{personalInfo.phone}</span>
            {personalInfo.location && <span>•</span>}
            <span>{personalInfo.location}</span>
            {personalInfo.linkedin && (
              <>
                <span>•</span>
                <a
                  href={personalInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  LinkedIn
                </a>
              </>
            )}
            {personalInfo.website && (
              <>
                <span>•</span>
                <a
                  href={personalInfo.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {
                    personalInfo.website
                      .replace(/^https?:\/\/(www\.)?/, "")
                      .split("/")[0]
                  }
                </a>
              </>
            )}
            {personalInfo.github && (
              <>
                <span>•</span>
                <a
                  href={personalInfo.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  GitHub
                </a>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Summary Section */}
      {summary && (
        <section
          className="space-y-0.5 relative z-10"
          style={{ pageBreakInside: "avoid" }}
        >
          <h2 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-0.5 leading-tight">
            Professional Summary
          </h2>
          <p className="text-xs text-gray-700 leading-snug">{summary}</p>
        </section>
      )}

      {/* Skills Section */}
      {skills?.sections && Object.keys(skills.sections).length > 0 && (
        <section
          className="space-y-2 relative z-10 w-full"
          style={{ pageBreakInside: "avoid" }}
        >
          <h2 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-0.5 leading-tight">
            Skills
          </h2>
          <div className="space-y-2">
            {Object.entries(skills.sections).map(([title, skillsList]) => (
              <div key={title} className="text-xs">
                <span className="font-semibold ml-4">{title}:</span>{" "}
                <span className="text-gray-700 px-3 font-semibold">
                  {skillsList.join(", ")}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Experience Section */}
      {experience && experience.length > 0 && (
        <section
          className="space-y-1.5 relative z-10 w-full"
          style={{ pageBreakInside: "avoid" }}
        >
          <h2 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-0.5 leading-tight">
            Experience
          </h2>
          {experience.map((exp) => (
            <div
              key={exp.id}
              className="space-y-0.5"
              style={{ pageBreakInside: "avoid" }}
            >
              <div className="flex justify-between ">
                <div>
                  <h3 className="text-xs font-semibold text-gray-900 leading-tight mb-0">
                    {exp.position}
                  </h3>
                  <p className="text-xs text-gray-900 leading-tight mb-0">
                    {exp.company} • {exp.location}
                  </p>
                </div>
                <span className="text-xs text-gray-900">
                  {exp.startDate ? formatDate(exp.startDate) : ""} -{" "}
                  {exp.currentlyWorking
                    ? "Present"
                    : exp.endDate
                    ? formatDate(exp.endDate)
                    : ""}
                </span>
              </div>
              <ul className="list-disc list-inside space-y-0.5">
                {exp.achievements.map((achievement, index) => (
                  <li
                    key={index}
                    className="text-xs text-gray-900 leading-snug ml-4"
                  >
                    {achievement}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {/* Projects Section */}
      {projects && projects.length > 0 && (
        <section
          className="space-y-1.5 relative z-10 w-full"
          style={{ pageBreakInside: "avoid" }}
        >
          <h2 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-0.5 leading-tight">
            Projects
          </h2>
          {projects.map((project) => (
            <div
              key={project.id}
              className="space-y-0.5"
              style={{ pageBreakInside: "avoid" }}
            >
              <div className="flex justify-between items-baseline">
                <h3 className="text-xs font-semibold text-gray-900 leading-tight flex-1 mb-0">
                  {project.name}
                </h3>
                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    View Project
                  </a>
                )}
              </div>
              <p className="text-xs text-gray-600 leading-tight">
                {project.technologies.join(" • ")}
              </p>
              <ul className="list-disc list-inside space-y-0.5">
                {(project.description || "")
                  .split("\n")
                  .filter((line) => line.trim() !== "")
                  .map((line, idx) => (
                    <li key={idx} className="text-xs text-gray-900 ml-4">
                      {line}
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {/* Education Section */}
      {education && education.length > 0 && (
        <section
          className="space-y-1.5 relative z-10 w-full"
          style={{ pageBreakInside: "avoid" }}
        >
          <h2 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-0.5 leading-tight">
            Education
          </h2>
          {education.map((edu) => (
            <div
              key={edu.id}
              className="space-y-0.5"
              style={{ pageBreakInside: "avoid" }}
            >
              <div className="flex justify-between items-baseline">
                <div>
                  <h3 className="text-xs font-semibold text-gray-900 leading-tight">
                    {edu.school}
                  </h3>
                  <p className="text-xs text-gray-900 leading-tight">
                    {edu.degree} in {edu.fieldOfStudy}
                  </p>
                </div>
                <span className="text-xs text-gray-900">
                  {edu.startDate ? formatDate(edu.startDate) : ""} -{" "}
                  {edu.endDate ? formatDate(edu.endDate) : "Present"}
                </span>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Certifications Section */}
      {certifications && certifications.length > 0 && (
        <section
          className="space-y-1.5 relative z-10 w-full"
          style={{ pageBreakInside: "avoid" }}
        >
          <h2 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-0.5 leading-tight">
            Certifications
          </h2>
          {certifications.map((cert) => (
            <div
              key={cert.id}
              className="space-y-0.5"
              style={{ pageBreakInside: "avoid" }}
            >
              <div className="flex justify-between items-baseline">
                <div>
                  <h3 className="text-xs font-semibold text-gray-900 leading-tight">
                    {cert.name}
                  </h3>
                  <p className="text-xs text-gray-600 leading-tight">
                    {cert.issuer}
                    {cert.certLink && (
                      <span className="ml-2">
                        •
                        <a
                          href={cert.certLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:underline"
                          title="View Certificate"
                        >
                          View Certificate
                        </a>
                      </span>
                    )}
                  </p>
                </div>
                <span className="text-xs text-gray-500">{cert.date}</span>
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
};

export default BaseTemplate;
